// import { v4 as uuidv4 } from 'uuid';

import { categoriesActions, categoriesSelectors, categoriesTypes } from 'app-components/flux/categories';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import store from 'app-components/utils/redux-store';

import deterministicGuid from './deterministicGuid';
import { mergeMintImportSharedState } from './mintImportSharedState';

const knownCategories = {
  1000000000: 'Auto & Transport',
  1000100000: 'Auto Insurance',
  1000150000: 'Auto Payment',
  1000300000: 'Gas & Fuel',
  1000400000: 'Parking',
  1000500000: 'Public Transportation',
  1000700000: 'Ride Share',
  1000800000: 'Service & Parts',

  7500000000: 'Bills & Utilities',
  // ???: 'Home Phone',
  7500375000: 'Internet',
  7500250000: 'Mobile Phone',
  // ??: 'Television',
  // ??: 'Utilities',

  // ??: 'Business Services',
  // ??: 'Advertising',
  // ??: 'Legal',
  // ??: 'Office Supplies',
  // ??: 'Printing',
  // ??: 'Shipping',

  2500000000: 'Education',
  2500250000: 'Books & Supplies',
  2500500000: 'Student Loan',
  2500750000: 'Tuition',

  2750000000: 'Entertainment',
  // ??: 'Amusement',
  // ??: 'Arts',
  // ??: 'Movies & DVDs',
  // ??: 'Music',
  // ??: 'Newspapers & Magazines',

  3000000000: 'Fees & Charges',
  3000175000: 'ATM Fee',
  // ??: 'Bank Fee',
  3000350000: 'Finance Charge',
  3000525000: 'Late Fee',
  3000700000: 'Service Fee',
  // ??: 'Trade Commissions',

  3250000000: 'Financial',
  3250350000: 'Financial Advisor',
  3250700000: 'Life Insurance',

  2250000000: 'Food & Dining',
  2250200000: 'Alcohol & Bars',
  2250400000: 'Coffee Shops',
  2250600000: 'Fast Food',
  // ??: 'Food Delivery',
  4000000000: 'Groceries',
  2250800000: 'Restaurants',

  3750000000: 'Gifts & Donations',
  2000000000: 'Charity',
  // ??: 'Gift',

  3500000000: 'Health & Fitness',
  4250150000: 'Dentist',
  4250300000: 'Doctor',
  4250450000: 'Eyecare',
  3500250000: 'Gym',
  4250600000: 'Health Insurance',
  4250750000: 'Pharmacy',
  // ??: 'Sports',

  4500000000: 'Home',
  4500100000: 'Furnishings',
  4500300000: 'Home Improvement',
  4500400000: 'Home Insurance',
  4500500000: 'Home Services',
  4500600000: 'Home Supplies',
  // ??: 'Lawn & Garden',
  4500800000: 'Mortgage & Rent',

  5750000000: 'Income',
  5750200000: 'Bonus',
  5750600000: 'Interest Income',
  5750700000: 'Paycheck',
  // ??: 'Reimbursement',
  // ??: 'Rental Income',
  // ??: 'Returned Purchase',

  // ??: 'Investments',
  // ??: 'Buy',
  // ??: 'Deposit',
  // ??: 'Dividend & Cap Gains',
  // ??: 'Sell',
  // ??: 'Withdrawal',

  5000000000: 'Kids',
  5000150000: 'Allowance',
  5000300000: 'Baby Supplies',
  5000450000: 'Babysitter & Daycare',
  5750300000: 'Child Support',
  5000750000: 'Kids Activities',
  5000900000: 'Toys',

  5250000000: 'Loans',
  5250175000: 'Loan Fees and Charges',
  5250350000: 'Loan Insurance',
  5250525035: 'Loan Interest',
  5250525000: 'Loan Payment',
  5250525070: 'Loan Principal',

  // ??: 'Misc Expenses',

  5500000000: 'Personal Care',
  5500020000: 'Hair',
  5500040000: 'Laundry',
  5500080000: 'Spa & Massage',

  6000000000: 'Pets',
  6000025000: 'Pet Food & Supplies',
  6000050000: 'Pet Grooming',
  6000075000: 'Veterinary',

  6750000000: 'Shopping',
  6750175000: 'Books',
  6750350000: 'Clothing',
  6750525000: 'Electronics & Software',
  // ??: 'Hobbies',
  // ??: 'Sporting Goods',

  7000000000: 'Taxes',
  7000100000: 'Federal Tax',
  7000200000: 'Local Tax',
  7000400000: 'Property Tax',
  7000500000: 'Sales Tax',
  7000800000: 'State Tax',

  7250000000: 'Travel',
  7250250000: 'Air Travel',
  7250500000: 'Hotel',
  7250750000: 'Rental Car & Taxi',
  // ??: 'Vacation',

  1750000000: 'Cash & ATM',
  // ??: 'Check',
};

const knownCOAs = {
  Uncategorized: { type: 'UNCATEGORIZED', id: '0' },
  Transfer: { type: 'BALANCE_ADJUSTMENT', id: '2' },
  'Credit Card Payment': { type: 'BALANCE_ADJUSTMENT', id: '3' },
  'Transfer for Cash Spending': { type: 'BALANCE_ADJUSTMENT', id: '2' },
};

const importCategories = async (fileJS) => {
  const key = 'CATEGORIES';
  let simplifiCOAsByMintId;
  try {
    const simplifiCategoriesById = categoriesSelectors.getResourcesById(store.getState());
    const simplifiCategoriesByMintName = simplifiCategoriesById.reduce((result, simplifiCategory) => {
      const mintCategoryName = knownCategories[simplifiCategory.knownCategoryId];
      if (mintCategoryName) {
        result[mintCategoryName] = simplifiCategory; // eslint-disable-line no-param-reassign
      }
      return result;
    }, {});

    // build known and unknown categories collections
    const { knownSimplifiCategoriesByMintId, unknownMintCategoriesByMintId, knownSimplifiCOAsByMintId } =
      fileJS.categories.reduce(
        (results, mintCategory) => {
          const simplifiCOA = knownCOAs[mintCategory.name];
          if (!mintCategory.isCustom && simplifiCOA) {
            results.knownSimplifiCOAsByMintId.set(mintCategory.id, chartOfAccountsTypes.mkChartOfAccount(simplifiCOA));
          }
          const simplifiCategory = simplifiCategoriesByMintName[mintCategory.name];
          if (!mintCategory.isCustom && simplifiCategory) {
            results.knownSimplifiCategoriesByMintId.set(mintCategory.id, simplifiCategory);
          } else {
            results.unknownMintCategoriesByMintId.set(mintCategory.id, {
              ...mintCategory,
              // clientId: uuidv4().toUpperCase(),
              clientId: deterministicGuid(mintCategory.id),
            });
          }
          return results;
        },
        {
          knownSimplifiCategoriesByMintId: new Map(),
          unknownMintCategoriesByMintId: new Map(),
          knownSimplifiCOAsByMintId: new Map(),
        },
      );

    mergeMintImportSharedState({ key, progress: 20 });

    const categoriesResponse = await new Promise((resolve, reject) => {
      store.dispatch(
        categoriesActions.batchCategories(
          [...unknownMintCategoriesByMintId.values()].map((mintCategory) => {
            const parentClientId = unknownMintCategoriesByMintId.get(mintCategory.parentId)?.clientId;
            const parentId = knownSimplifiCategoriesByMintId.get(mintCategory.parentId)?.id;
            return categoriesTypes.mkCategory({
              clientId: mintCategory.clientId,
              name: `${mintCategory.name} (mint)`,
              type: mintCategory.categoryType,
              parentId,
              parentClientId,

              isNotUserAssignable: mintCategory.isUnassignable,
              isExcludedFromReports: mintCategory.isUntrendable || mintCategory.isIgnored,
              isExcludedFromBudgets: mintCategory.isUnbudgetable || mintCategory.isIgnored,

              description: mintCategory.id, // kinda hack
            });
          }),
          { resolve, reject },
        ),
      );
    });

    mergeMintImportSharedState({ key, progress: 80 });

    simplifiCOAsByMintId = categoriesResponse.data.items.reduce((simplifiCOAs, simplifiCategory) => {
      const mintCategoryFound = [...unknownMintCategoriesByMintId.values()].find(
        (mintCategory) => mintCategory.clientId === simplifiCategory.resource.clientId,
      );
      if (assert(mintCategoryFound, 'mint category not found', { simplifiCategory })) {
        const id =
          (simplifiCategory.code === 409 && simplifiCategory.errors?.[0]?.extData?.conflictId) ||
          simplifiCategory.resource?.resource?.id ||
          simplifiCategory.resource?.id;
        if (assert(id, 'simplifi category id is not found', { simplifiCategory })) {
          simplifiCOAs.set(
            mintCategoryFound.id,
            chartOfAccountsTypes.mkChartOfAccount({
              type: 'CATEGORY',
              id,
            }),
          );
        }
      }
      return simplifiCOAs;
    }, new Map());
    knownSimplifiCategoriesByMintId.forEach((simplifiCategory, mintCategoryId) =>
      simplifiCOAsByMintId.set(
        mintCategoryId,
        chartOfAccountsTypes.mkChartOfAccount({
          type: 'CATEGORY',
          id: simplifiCategory.id,
        }),
      ),
    );
    // override some simple categories with critical COAs
    knownSimplifiCOAsByMintId.forEach((simplifiCOA, mintCategoryId) =>
      simplifiCOAsByMintId.set(mintCategoryId, simplifiCOA),
    );

    mergeMintImportSharedState({ key, progress: 100, [key]: { importedCount: simplifiCOAsByMintId.size } });
  } catch (error) {
    assert(false, error);
    mergeMintImportSharedState({ error: key, [key]: { error } });
  }

  return simplifiCOAsByMintId;
};

export default importCategories;
